/** Global overlay while GitHub API requests are in flight (reference-counted, debounced). */

/** Only show overlay if a GitHub operation is still in flight after this long. */
const SHOW_DELAY_MS = 1000;

let depth = 0;
/** When > 0, GitHub calls run but the global overlay is suppressed (page already shows a loader). */
let suppressDepth = 0;
let showTimer: ReturnType<typeof setTimeout> | null = null;
let overlay: HTMLElement | null = null;
let statusEl: HTMLElement | null = null;

export function pushGitBusySuppress(): void {
  suppressDepth++;
}

export function popGitBusySuppress(): void {
  suppressDepth = Math.max(0, suppressDepth - 1);
}

/** Run GitHub work without the global “Syncing with GitHub…” overlay. */
export async function withoutGitBusyOverlay<T>(fn: () => Promise<T>): Promise<T> {
  pushGitBusySuppress();
  try {
    return await fn();
  } finally {
    popGitBusySuppress();
  }
}

export function mountGitBusyOverlay(root: HTMLElement = document.body): void {
  if (overlay) return;
  overlay = document.createElement('div');
  overlay.id = 'git-busy-overlay';
  overlay.className = 'git-busy-overlay hidden';
  overlay.setAttribute('aria-hidden', 'true');
  overlay.innerHTML = `
    <div class="git-busy-card" role="status" aria-live="polite">
      <div class="git-busy-spinner app-spinner" aria-hidden="true"></div>
      <p class="git-busy-label">Syncing with GitHub…</p>
    </div>
  `;
  statusEl = overlay.querySelector('.git-busy-label');
  root.appendChild(overlay);
}

export function setGitBusyMessage(message: string): void {
  if (statusEl) statusEl.textContent = message;
}

function setVisible(visible: boolean): void {
  if (!overlay) return;
  overlay.classList.toggle('hidden', !visible);
  overlay.setAttribute('aria-hidden', visible ? 'false' : 'true');
}

function begin(): boolean {
  if (suppressDepth > 0) return false;
  depth++;
  if (depth === 1 && !showTimer) {
    showTimer = setTimeout(() => {
      showTimer = null;
      if (depth > 0) setVisible(true);
    }, SHOW_DELAY_MS);
  }
  return true;
}

function end(tracked: boolean): void {
  if (!tracked) return;
  depth = Math.max(0, depth - 1);
  if (depth === 0) {
    if (showTimer) {
      clearTimeout(showTimer);
      showTimer = null;
    }
    setVisible(false);
    setGitBusyMessage('Syncing with GitHub…');
  }
}

export async function withGitBusy<T>(fn: () => Promise<T>, message?: string): Promise<T> {
  if (message) setGitBusyMessage(message);
  const tracked = begin();
  try {
    return await fn();
  } finally {
    end(tracked);
  }
}
